import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Panel from "../components/Panel";
import Kpi from "../components/Kpi";
import { fmtNum, statusClass } from "../lib/format";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COL = ["#A5300F", "#E5C243", "#4A5A6A", "#3E7C4F", "#C75A3A", "#6FA77E"];

export default function Identity() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/identity/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;

  const radarData = d.domains.map((x) => ({ domain: x.name.split(" ")[0], score: x.score }));

  return (
    <>
      <Masthead
        module="identity"
        eyebrow="Identity & Access Assurance"
        title="Identity Health"
        subtitle="Vendor-agnostic · normalised across all identity providers, directories & PAM"
        meta={<div>Identity population: <strong>{fmtNum(d.population)} identities</strong><br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="cv-panel">
            <div className="text-[12px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>Composite Identity Health</div>
            <div className="font-serif-display text-[72px] leading-none mt-2" style={{ color: "var(--cv-crimson)" }}>
              {d.composite_score}<span className="text-[24px] opacity-60">/100</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <div className="cv-tile cv-tile-blue" style={{ cursor: "default" }}>
                <div className="cv-tile-label">Current</div>
                <div className="cv-tile-val">{d.composite_score}</div>
              </div>
              <div className="cv-tile cv-tile-green" style={{ cursor: "default" }}>
                <div className="cv-tile-label">6-month gain</div>
                <div className="cv-tile-val">+11</div>
              </div>
              <div className="cv-tile cv-tile-red" style={{ cursor: "default" }}>
                <div className="cv-tile-label">Below target</div>
                <div className="cv-tile-val">2</div>
              </div>
            </div>
          </div>

          <Panel testid="panel-trend" title="Health Trend" subtitle="Composite score, trailing six months">
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={d.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[60, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#A5300F" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-balance" title="Domain Balance" subtitle="Score by identity domain (higher = healthier)">
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E7DCCD" />
                  <PolarAngleAxis dataKey="domain" tick={{ fontSize: 10 }} />
                  <Radar dataKey="score" stroke="#E5C243" fill="#E5C243" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <Panel
          testid="panel-population"
          title="Identity Population"
          subtitle="Composition of the managed identity estate"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div className="h-[170px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.composition} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75}>
                    {d.composition.map((e) => <Cell key={e.label} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {d.composition.map((c, i) => (
              <button
                key={c.label}
                data-testid={`pop-${c.label}`}
                onClick={() => nav(`/drill/identities?kind=${c.label.includes("Human") ? "human" : c.label.includes("Service") ? "service" : "guest"}`)}
                className="cv-tile text-left"
                style={{ background: "var(--cv-sand)" }}
              >
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }}/><div className="cv-tile-label">{c.label}</div></div>
                <div className="cv-tile-val">{fmtNum(c.value)}</div>
              </button>
            ))}
          </div>
        </Panel>

        <div className="cv-section-label mt-6">Identity Domains</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {d.domains.map((dom) => (
            <Panel key={dom.id} testid={`domain-${dom.id}`} title={dom.name} subtitle={`Score: ${dom.score}/100`}>
              <div className="grid grid-cols-2 gap-2">
                {dom.items.map((it, i) => (
                  <button
                    key={it.label}
                    onClick={() => nav(`/drill/identities`)}
                    data-testid={`dom-${dom.id}-item-${i}`}
                    className="cv-tile cv-tile-blue text-left"
                  >
                    <div className="cv-tile-label">{it.label}</div>
                    <div className="cv-tile-val">{it.value}</div>
                    <div className="cv-tile-sub">{it.sub} · <span style={{ color: "var(--cv-green)" }}>{it.trend}</span></div>
                  </button>
                ))}
              </div>
            </Panel>
          ))}
        </div>

        <div className="cv-section-label mt-6">Identity Risk Watchlist</div>
        <Panel testid="panel-watchlist" title="Highest-standing items requiring action" subtitle="Across all domains — click to drill">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                <th className="text-left px-3 py-2 small-caps font-bold">Item</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Domain</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Exposure</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Standing</th>
              </tr>
            </thead>
            <tbody>
              {d.watchlist.map((w, i) => (
                <tr
                  key={w.id}
                  className="cv-row-clickable"
                  data-testid={`watch-row-${i}`}
                  onClick={() => {
                    if (w.item.includes("Leaked")) nav("/drill/identities?leaked=true");
                    else if (w.item.includes("Dormant")) nav("/drill/identities?dormant=true");
                    else if (w.item.includes("global admins")) nav("/drill/identities?privileged=true");
                    else nav("/drill/identities");
                  }}
                >
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{w.item}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{w.domain}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{w.exposure}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                    <span className={`status-pill ${statusClass(w.standing)}`}>{w.standing}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
}
